import { useEffect, useState, useRef } from "react";
import { Upload, Check, X, Clock, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  pending: { bg: "rgba(245,158,11,0.10)", color: "#B45309", label: "Pending Review" },
  approved: { bg: "rgba(31,157,85,0.10)", color: "#166534", label: "Approved" },
  revision_requested: { bg: "rgba(220,38,38,0.08)", color: "#B91C1C", label: "Revision Requested" },
};

const ArtworkPanel = ({ projectId }) => {
  const { user, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [decisionOn, setDecisionOn] = useState(null); // artwork id being reviewed
  const [comment, setComment] = useState("");
  const inputRef = useRef(null);

  const load = () => api.get(`/artwork/project/${projectId}`).then((r) => setItems(r.data.artwork)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25 MB limit"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/artwork/upload?project_id=${projectId}&label=${encodeURIComponent(file.name)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Artwork uploaded");
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const decide = async (artworkId, decision) => {
    try {
      await api.post(`/artwork/${artworkId}/decision`, { decision, comment: comment || null });
      toast.success(decision === "approved" ? "Artwork approved" : "Revision requested");
      setDecisionOn(null); setComment("");
      load();
    } catch { toast.error("Update failed"); }
  };

  const downloadUrl = (art) => {
    const token = localStorage.getItem("flowtalis_token");
    return `${API}/artwork/${art.id}/download?auth=${encodeURIComponent(token)}`;
  };

  const isImage = (ct) => ct?.startsWith("image/");

  return (
    <div className="ft-card" data-testid="artwork-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="ft-eyebrow">Artwork Center</div>
          <h3 className="text-lg font-semibold mt-1">Version history &amp; approvals</h3>
        </div>
        <div>
          <input ref={inputRef} type="file" className="hidden" onChange={onUpload}
                 accept="image/*,application/pdf,.psd,.ai,.eps" data-testid="artwork-file-input" />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="ft-btn-primary"
            data-testid="artwork-upload-btn"
          >
            {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading…</> : <><Upload size={16} /> Upload New Version</>}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-10 text-[var(--ft-muted)] text-sm">
          No artwork uploaded yet. Upload the first version to begin the approval flow.
        </div>
      ) : (
        <div className="space-y-3" data-testid="artwork-list">
          {items.map((a) => {
            const style = STATUS_STYLES[a.status] || STATUS_STYLES.pending;
            const canDecide = !isAdmin || user?.role === "founder"; // client decides; founder can also on behalf
            return (
              <div key={a.id} className="border border-[var(--ft-border)] rounded-xl p-4" data-testid={`artwork-item-${a.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-[var(--ft-soft-gray)] border border-[var(--ft-border)]">
                    {isImage(a.content_type) ? (
                      <img src={downloadUrl(a)} alt={a.label} className="w-full h-full object-cover" />
                    ) : (
                      <FileText size={22} style={{ color: "var(--ft-blue)" }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold">v{a.version} · {a.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                            style={{ background: style.bg, color: style.color }}>
                        {style.label}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--ft-muted)] mt-1">
                      {a.original_filename} · {(a.size / 1024).toFixed(0)} KB · uploaded by {a.uploaded_by_name} ({a.uploaded_by_role})
                    </div>
                    {a.review_comment && (
                      <div className="mt-2 text-sm bg-[var(--ft-soft-gray)] rounded-lg p-2">
                        <span className="text-xs text-[var(--ft-muted)]">Reviewer:</span> {a.review_comment}
                      </div>
                    )}
                  </div>
                  <a href={downloadUrl(a)} target="_blank" rel="noreferrer" className="ft-btn-ghost text-sm" data-testid={`artwork-download-${a.id}`}>
                    Download
                  </a>
                </div>

                {a.status === "pending" && canDecide && (
                  <div className="mt-4 pt-4 border-t border-[var(--ft-border)]">
                    {decisionOn === a.id ? (
                      <div className="space-y-3">
                        <textarea
                          className="ft-input"
                          rows={2}
                          placeholder="Optional comment for the design team…"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          data-testid={`artwork-comment-${a.id}`}
                        />
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => { setDecisionOn(null); setComment(""); }} className="ft-btn-ghost">Cancel</button>
                          <button onClick={() => decide(a.id, "revision_requested")} className="ft-btn-secondary" data-testid={`artwork-revise-${a.id}`}>
                            <X size={14} /> Request Revision
                          </button>
                          <button onClick={() => decide(a.id, "approved")} className="ft-btn-primary" data-testid={`artwork-approve-${a.id}`}>
                            <Check size={14} /> Approve
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setDecisionOn(a.id)} className="ft-btn-secondary text-sm" data-testid={`artwork-review-${a.id}`}>
                          <Clock size={14} /> Review this version
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ArtworkPanel;
